import { useState } from "react";
import { toast } from "react-toastify";
import useLoader from "../../../hooks/useLoader.js";
import { formatDisplayPeriod } from "../../../Helper/functionHelper.js";
import useModal from "../../../hooks/useModal.js";
import useConfirm from "../../../hooks/useConfirm.js";
import useAuthUser from "../../../hooks/useAuthUser.js";
import { PAYROLL_STATUS, PAYROLL_STATUS_LABELS, approvePayroll, rejectPayroll, holdPayroll } from "../../../services/payrollPeriodic.js";
import PayheadBreakdownView from "../shared/PayheadBreakdownView.jsx";

const payableUnitsLabel = (payBasis) => (payBasis === "HOURLY" ? "Total Payable Hours" : "Total Payable Days");

const DetailRow = ({ label, value }) => (
  <div className="col-6 col-md-4 mb-2">
    <div className="text-muted" style={{ fontSize: "11px" }}>
      {label}
    </div>
    <div className="fw-semibold" style={{ fontSize: "13px" }}>
      {value}
    </div>
  </div>
);

const PayrollDetail = ({ refresh, record }) => {
  const { isAdminHrRole, isFinanceManagerRole } = useAuthUser();
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();
  const { confirm } = useConfirm();
  const [isBusy, setIsBusy] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [showHoldInput, setShowHoldInput] = useState(false);

  const workedUnits = record.payroll_detail?.worked_units;
  const payheads = record.payroll_detail?.payheads || [];
  const manualDeductions = record.payroll_detail?.manual_deductions || [];
  const manualEarnings = record.payroll_detail?.manual_earnings || [];

  const runAction = async (fn, successMessage) => {
    setIsBusy(true);
    showLoader();
    try {
      await fn();
      toast.success(successMessage);
      refresh?.();
      hideModal?.();
    } catch (error) {
      console.error("Error updating payroll record:", error);
      toast.error(error?.response?.data?.message || "Action failed");
    } finally {
      setIsBusy(false);
      hideLoader();
    }
  };

  const handleApprove = async () => {
    const isConfirmed = await confirm({
      title: "Approve Payroll",
      content: `Approve payroll of ₹${Number(record.net_pay).toFixed(2)} for ${record.employee_name}?`,
      confirmText: "Yes, Approve",
      cancelText: "Cancel",
    });
    if (!isConfirmed) return;
    runAction(() => approvePayroll(record.id), "Payroll approved successfully!");
  };

  const handleReject = async () => {
    const isConfirmed = await confirm({
      title: "Reject Payroll",
      content: `Reject this payroll record for ${record.employee_name}?`,
      confirmText: "Yes, Reject",
      cancelText: "No",
    });
    if (!isConfirmed) return;
    runAction(() => rejectPayroll(record.id), "Payroll rejected successfully!");
  };

  const handleHoldToggle = async () => {
    if (Number(record.is_hold) === 1) {
      runAction(() => holdPayroll(record.id, false, null), "Payroll unheld successfully!");
      return;
    }

    if (!showHoldInput) {
      setShowHoldInput(true);
      return;
    }

    if (!holdReason.trim()) {
      toast.error("A reason is required to hold this payroll record");
      return;
    }

    runAction(() => holdPayroll(record.id, true, holdReason.trim()), "Payroll held successfully!");
  };

  const isRejected = Number(record.status) === PAYROLL_STATUS.REJECTED;
  // Approve/Hold are Finance Manager-only. Reject can also be triggered by
  // the Admin/HR who generated the record, to cancel it before Finance acts.
  const canApprove = isFinanceManagerRole && Number(record.status) === PAYROLL_STATUS.GENERATED;
  const canReject =
    (isFinanceManagerRole || isAdminHrRole) &&
    [PAYROLL_STATUS.DRAFT, PAYROLL_STATUS.GENERATED].includes(Number(record.status));
  // No status-changing action applies to a rejected record - it was
  // thrown out, not locked in place. The only way forward is generating
  // a new payroll for this same period (Generate Payroll screen).
  const canHold = isFinanceManagerRole && !isRejected;

  return (
    <div style={{ marginTop: "10px" }}>
      <div className="row">
        <DetailRow label="Employee" value={`${record.employee_name} (${record.employee_code})`} />
        <DetailRow label="Period" value={formatDisplayPeriod(record.period_start, record.period_end)} />
        <DetailRow label="Status" value={PAYROLL_STATUS_LABELS[Number(record.status)] || "Unknown"} />
      </div>

      {workedUnits && (
        <div className="p-3 mb-3" style={{ backgroundColor: "#f9f9f9", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
          <div className="fw-semibold mb-2" style={{ fontSize: "13px" }}>
            Worked Units Breakdown
          </div>
          <div className="row">
            <DetailRow label="Days Present" value={workedUnits.daysPresent} />
            <DetailRow label="Hours Worked" value={workedUnits.hoursWorked} />
            <DetailRow label="Week-off Days" value={workedUnits.weekOffDays} />
            <DetailRow label="Holiday Days" value={workedUnits.holidayDays} />
            <DetailRow label="Leave Days" value={workedUnits.leaveDays} />
            <DetailRow label="LWP Days" value={workedUnits.lwpDays} />
            <DetailRow label="Pay Basis" value={record.pay_basis} />
            <DetailRow label={payableUnitsLabel(record.pay_basis)} value={record.units_worked} />
            <DetailRow label="Rate Applied" value={`₹${Number(record.rate_applied).toFixed(2)}`} />
          </div>
        </div>
      )}

      <PayheadBreakdownView payheads={payheads} />

      {manualEarnings.length > 0 && (
        <div className="mb-3">
          <div className="fw-semibold mb-1" style={{ fontSize: "13px" }}>
            Manual Earnings
          </div>
          <ul className="mb-0" style={{ fontSize: "13px" }}>
            {manualEarnings.map((e, i) => (
              <li key={i}>
                {e.label}: ₹{Number(e.amount).toFixed(2)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {manualDeductions.length > 0 && (
        <div className="mb-3">
          <div className="fw-semibold mb-1" style={{ fontSize: "13px" }}>
            Manual Deductions
          </div>
          <ul className="mb-0" style={{ fontSize: "13px" }}>
            {manualDeductions.map((d, i) => (
              <li key={i}>
                {d.label}: ₹{Number(d.amount).toFixed(2)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="row" style={{ fontSize: "14px" }}>
        <div className="col-4 fw-semibold">Total Income: ₹{Number(record.total_income).toFixed(2)}</div>
        <div className="col-4 fw-semibold">Deduction: ₹{Number(record.deduction).toFixed(2)}</div>
        <div className="col-4 fw-semibold text-success">Net Pay: ₹{Number(record.net_pay).toFixed(2)}</div>
      </div>

      {record.is_hold === 1 && record.hold_reason && (
        <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: "12px" }}>
          On hold: {record.hold_reason}
        </div>
      )}

      {isRejected && (
        <div className="alert alert-secondary mt-3 mb-0" style={{ fontSize: "12px" }}>
          This payroll was rejected - no further action can be taken on it here. Go to Generate Payroll and
          generate again for this same period to create a fresh one.
        </div>
      )}

      {showHoldInput && Number(record.is_hold) !== 1 && (
        <div className="mt-3 p-2" style={{ backgroundColor: "#fff8e6", border: "1px solid #ffc107", borderRadius: "6px" }}>
          <label className="form-label fw-semibold mb-1" style={{ fontSize: "12px" }}>
            Reason for hold
          </label>
          <input
            type="text"
            autoFocus
            className="form-control form-control-sm"
            placeholder="Enter a reason for holding this payroll"
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
          />
        </div>
      )}

      {(isFinanceManagerRole || isAdminHrRole) && !isRejected && (
        <div className="d-flex justify-content-end gap-2 flex-wrap mt-3" style={{ borderTop: "1px solid #e0e0e0", paddingTop: "12px" }}>
          {canHold && (
            <button type="button" className="btn btn-outline-warning btn-sm" disabled={isBusy} onClick={handleHoldToggle}>
              {Number(record.is_hold) === 1 ? "Unhold" : showHoldInput ? "Confirm Hold" : "Hold"}
            </button>
          )}
          {canReject && (
            <button type="button" className="btn btn-outline-danger btn-sm" disabled={isBusy} onClick={handleReject}>
              Reject
            </button>
          )}
          {canApprove && (
            <button type="button" className="btn btn-success btn-sm" disabled={isBusy} onClick={handleApprove}>
              Approve
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PayrollDetail;
